import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faLayerGroup, faSpinner } from '@fortawesome/free-solid-svg-icons';
import { useAppContext } from '../../../context/AppContext';
import { fetchData } from '../../../services';
import { useEffect, useMemo, useState } from 'react';
import Modal from '../../helpers/Modal';
const getIncludedDesignIds = (record) => {
    const raw = record?.included_designs ?? record?.includedDesigns;
    if (!raw) return [];
    return Array.isArray(raw) ? raw : [];
};

const getRecordLabel = (type, record) => {
    if (type === 'quote') return record?.quote_number ?? `Quote ${record?.id}`;
    return record?.invoice_number ?? `Invoice ${record?.id}`;
};

const getUpdateUrl = (type, record, baseUrl) => {
    if (type === 'quote') {
        return `${baseUrl}/apis/billing/${record.id}/included-designs`;
    }
    return `${baseUrl}/apis/billing/invoices/${record.id}/included-designs`;
};

export const ManageIncludedDesignsForm = ({
    type,
    record,
    designs: designsProp = [],
    setQuotes,
    isOpen: controlledOpen,
    onClose: controlledOnClose,
}) => {
    const { token, base_url, setInvoices } = useAppContext();
    const [internalOpen, setInternalOpen] = useState(false);
    const isControlled = controlledOnClose != null;
    const isModalOpen = isControlled ? controlledOpen : internalOpen;
    const [selectedIds, setSelectedIds] = useState([]);
    const [availableDesigns, setAvailableDesigns] = useState(designsProp);
    const [isLoadingDesigns, setIsLoadingDesigns] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [error, setError] = useState('');

    const projectId = record?.project_site_id;

    useEffect(() => {
        if (!isModalOpen) return;

        setSelectedIds(getIncludedDesignIds(record));
        setError('');

        if (designsProp.length > 0) {
            setAvailableDesigns(designsProp);
            return;
        }

        if (!projectId || !token) {
            setAvailableDesigns([]);
            return;
        }

        let cancelled = false;
        setIsLoadingDesigns(true);

        fetchData(token, `${base_url}/apis/designs/${projectId}`)
            .then((response) => {
                if (cancelled) return;
                setAvailableDesigns(Array.isArray(response?.designs) ? response.designs : []);
            })
            .catch((err) => {
                console.error('Error loading project designs:', err);
                if (!cancelled) {
                    setAvailableDesigns([]);
                }
            })
            .finally(() => {
                if (!cancelled) {
                    setIsLoadingDesigns(false);
                }
            });

        return () => {
            cancelled = true;
        };
    }, [isModalOpen, record, designsProp, projectId, token, base_url]);

    const designOptions = useMemo(() => {
        const byUniqueId = new Map();

        availableDesigns.forEach((design) => {
            if (design?.unique_id) {
                byUniqueId.set(design.unique_id, design);
            }
        });

        getIncludedDesignIds(record).forEach((uid) => {
            if (!byUniqueId.has(uid)) {
                byUniqueId.set(uid, { unique_id: uid, design_name: null });
            }
        });

        return Array.from(byUniqueId.values()).sort((a, b) => {
            const nameA = (a.design_name || a.unique_id || '').toLowerCase();
            const nameB = (b.design_name || b.unique_id || '').toLowerCase();
            return nameA.localeCompare(nameB);
        });
    }, [availableDesigns, record]);

    const openModal = () => {
        if (!isControlled) {
            setInternalOpen(true);
        }
    };

    const closeModal = () => {
        if (isControlled) {
            controlledOnClose?.();
        } else {
            setInternalOpen(false);
        }
    };

    const toggleDesign = (uniqueId) => {
        setSelectedIds((prev) =>
            prev.includes(uniqueId)
                ? prev.filter((id) => id !== uniqueId)
                : [...prev, uniqueId]
        );
    };

    const applyLocalUpdate = (includedDesigns) => {
        if (type === 'quote' && setQuotes) {
            setQuotes((prevQuotes) =>
                prevQuotes.map((quote) =>
                    quote.id === record.id
                        ? { ...quote, included_designs: includedDesigns }
                        : quote
                )
            );
            return;
        }

        if (type === 'invoice') {
            setInvoices((prevInvoices) =>
                prevInvoices.map((invoice) =>
                    invoice.id === record.id
                        ? { ...invoice, included_designs: includedDesigns }
                        : invoice
                )
            );
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (isSubmitting) return;

        setError('');
        setIsSubmitting(true);

        const payload = { included_designs: selectedIds };

        try {
            const response = await fetch(getUpdateUrl(type, record, base_url), {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: token,
                },
                body: JSON.stringify(payload),
            });

            const data = await response.json().catch(() => ({}));

            if (!response.ok) {
                const err = new Error('Request failed');
                err.status = response.status;
                throw err;
            }

            const updatedRecord =
                data?.quote ??
                data?.invoice ??
                data?.[type] ??
                null;

            const includedDesigns =
                updatedRecord?.included_designs ??
                updatedRecord?.includedDesigns ??
                selectedIds;

            applyLocalUpdate(includedDesigns);

            window.showAlert?.(200, 'Included designs updated successfully.');
            closeModal();
        } catch (err) {
            console.error('Error updating included designs:', err);
            const status = err?.status ?? 500;
            if (status === 404) {
                window.showAlert?.(404, `${type === 'quote' ? 'Quote' : 'Invoice'} not found.`);
            } else if (status >= 400 && status < 500) {
                setError('Invalid request. Please review your selections and try again.');
            } else {
                window.showAlert?.(500, 'Failed to update included designs. Please try again.');
            }
        } finally {
            setIsSubmitting(false);
        }
    };

    const recordLabel = getRecordLabel(type, record);
    const documentLabel = type === 'quote' ? 'Quote' : 'Invoice';

    const formContent = (
        <form onSubmit={handleSubmit}>
            <h2>Manage Included Designs</h2>
            <p>
                {documentLabel}: <strong>{recordLabel}</strong>
            </p>
            <p>Select which designs appear on this {documentLabel.toLowerCase()}.</p>

            {isLoadingDesigns ? (
                <p>
                    <FontAwesomeIcon icon={faSpinner} spin /> Loading designs...
                </p>
            ) : designOptions.length === 0 ? (
                <p>No designs are available for this project.</p>
            ) : (
                <div className="form-group">
                    {designOptions.map((design) => {
                        const uniqueId = design.unique_id;
                        const label = design.design_name
                            ? `${design.design_name} (${uniqueId})`
                            : uniqueId;

                        return (
                            <label
                                key={uniqueId}
                                className="checkbox-label checkbox-label--block"
                            >
                                <input
                                    type="checkbox"
                                    checked={selectedIds.includes(uniqueId)}
                                    onChange={() => toggleDesign(uniqueId)}
                                    disabled={isSubmitting}
                                />
                                {label}
                                {!design.design_name && (
                                    <span className="form-hint--inline">
                                        (not found on this project)
                                    </span>
                                )}
                            </label>
                        );
                    })}
                </div>
            )}

            {error && (
                <p className="form-error">
                    {error}
                </p>
            )}

            <div className="form-actions">
                <button
                    type="button"
                    onClick={closeModal}
                    className="btn btn-secondary"
                    disabled={isSubmitting}
                >
                    Cancel
                </button>
                <button
                    type="submit"
                    className="btn btn-primary"
                    disabled={isSubmitting || isLoadingDesigns}
                >
                    {isSubmitting ? (
                        <>
                            <FontAwesomeIcon icon={faSpinner} spin /> Saving...
                        </>
                    ) : (
                        'Save Designs'
                    )}
                </button>
            </div>
        </form>
    );

    const setModalOpen = isControlled
        ? (open) => {
              if (!open) controlledOnClose?.();
          }
        : setInternalOpen;

    return (
        <>
            {!isControlled && (
                <button
                    type="button"
                    className="btn btn-action btn-secondary"
                    title="Manage included designs"
                    onClick={openModal}
                >
                    <FontAwesomeIcon icon={faLayerGroup} /> Manage Designs
                </button>
            )}
            {isModalOpen && (
                <Modal form={formContent} setIsModalOpen={setModalOpen} />
            )}
        </>
    );
};
